import { Text, Flex, Input, Textarea } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useEffect, useMemo, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { SearchableSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import GemsService from '../../../services/Gems/GemsService'
import { IGem, IGemUpdatePayload } from '../../../services/Gems/interfaces'
import { ISector } from '../../../services/Sector/SectorService'

interface IProps {
  isOpen: boolean
  onClose: () => void
  sectors: ISector[]
  gem: IGem
}

export function GemsEditModal({ isOpen, onClose, sectors, gem }: IProps) {
  const gemsService = useMemo(() => new GemsService(), [])
  const [form, setForm] = useState<IGemUpdatePayload>({
    name: gem.name,
    description: gem.description,
    url: gem.url,
    sector_ids: gem.sectors.map((sector) => sector.id)
  })
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({})
  const [saving, setSaving] = useState(false)
  const toast = useAppToast()

  useEffect(() => {
    if (!isOpen) {
      setForm({
        name: gem.name,
        description: gem.description,
        url: gem.url,
        sector_ids: gem.sectors.map((sector) => sector.id)
      })
      setErrors({})
    }
  }, [isOpen, gem])

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!formValid()) {
      toast.error('Formulário inválido', 'Preencha todos os campos para criar um Agente de IA.')
      return
    }

    try {
      const response = await gemsService.update(gem.id, form)

      toast.success('Agente de IA atualizado', `O Agente de IA "${response.gem.name}" foi atualizado com sucesso.`)

      onClose()
    } catch (err) {
      const error = err as AxiosError<{ errors: Record<string, string[]> }>
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors)
        toast.error('Erro de validação', 'Insira os parâmetros corretamente.')
      } else {
        toast.error('Erro inesperado', 'Ocorreu um erro ao atualizar o Agente de IA.')
      }
    } finally {
      setSaving(false)
    }
  }

  const formValid = () => {
    return form.name && form.description && form.url && form.sector_ids && form.sector_ids.length > 0
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Agente de IA"
      onSubmit={handleSave}
      isLoading={saving}
      isConfirmDisabled={!formValid()}
    >
      <Flex direction="column">
        <FormField label="Nome" error={errors.name} isRequired>
          <Input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FormField>

        <FormField label="Descrição" error={errors.description} isRequired mt={4}>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </FormField>

        <FormField label="URL" error={errors.url} isRequired mt={4}>
          <Input type="text" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
        </FormField>

        <FormField label="Selecione os setores relacionados" error={errors.sector_ids} mt={4}>
          <SearchableSelect<number>
            isMulti
            options={sectors?.map((s) => ({ value: s.id, label: s.name })) || []}
            value={form.sector_ids ?? []}
            onChange={(sectorIds) => setForm({ ...form, sector_ids: sectorIds })}
            placeholder="Selecione um ou mais setores"
            entity="setor"
          />
        </FormField>
      </Flex>
      {!formValid() && (
        <Text variant="caption" mt={4}>
          Preencha todos os campos acima para prosseguir
        </Text>
      )}
    </FormModal>
  )
}
